import React from 'react';
import { color_colors_ocean, color_text_light } from '../../constants/colors';
import ConditionalTooltipWrapper from '../general/ConditionalTooltipWrapper';
import styled from '@emotion/styled';

const deltaDown = require('../../content/images/analytics-icons/delta-down.svg');
const deltaUp = require('../../content/images/analytics-icons/delta-up.svg');

interface TotalProps {
    title: string
    subtitle?: string
    value: string | number | string[]
    deltaValue?: number
    deltaPercentage?: number
    showValue?: boolean
    showPercentage?: boolean
    tooltip?: string
    icon?: string
    secondIcon?: string
    className?: string
    showValueAsPercent?: boolean
    hasSpanishCalls?: boolean
    isActionCompleteHealthCareCard?: boolean
    subValue?: string | React.ReactNode
    subValueSpanish?: string | React.ReactNode
    subValue2?: string | React.ReactNode
    subValue2Spanish?: string | React.ReactNode;
    subValue3?: string | React.ReactNode
    subValue3Spanish?: string | React.ReactNode;
    subValue4?: string | React.ReactNode
    subValue4Spanish?: string | React.ReactNode;
    highlight?: boolean
    tooltipMaxWidth?: number
    onClick?: () => void
}


const TotalCard: React.FC<TotalProps> = (props) => {
    const content = (
        <ConditionalTooltipWrapper
            place='top'
            maxWidth={props.tooltipMaxWidth ? props.tooltipMaxWidth : undefined}
            showTooltip={!!props.tooltip?.length}
            text={props.tooltip}
        >
            <TotalItem highlight={props.highlight}>
                <ItemLabel>{props.title}</ItemLabel>
                {props.subtitle && <ItemLabel>{props.subtitle}</ItemLabel>}
                {props.icon && (
                    <IconWrapper>
                        <StyledIcon src={props.icon} />
                        {props.secondIcon && <StyledIcon src={props.secondIcon} />}
                    </IconWrapper>
                )}
                <ItemValuesContainer>
                    {Array.isArray(props.value) ? (
                        props.value.length > 1 && props.value.map((val, index) => (
                            <ItemValue key={index}>{val}</ItemValue>
                        ))
                    ) : (
                        <ValueWrapper>
                            <ItemValue noIcon={!props.icon}>{props.value}{props.showValueAsPercent && '%'}</ItemValue>
                            {props.isActionCompleteHealthCareCard ? (
                                props.hasSpanishCalls ? (
                                    <SubValueWrapper>
                                        <ActionWrapper>
                                            <LanguageTitle></LanguageTitle>
                                            <SubValue>Scheduled</SubValue>
                                            <SubValue>Rescheduled</SubValue>
                                            <SubValue>Cancelled</SubValue>
                                            <SubValue>Checked Next Appt</SubValue>
                                        </ActionWrapper>
                                        <EnglishWrapper>
                                            <LanguageTitle>English</LanguageTitle>
                                            {props.subValue && <SubValue>{props.subValue} </SubValue>}
                                            {props.subValue2 && <SubValue>{props.subValue2} </SubValue>}
                                            {props.subValue3 && <SubValue>{props.subValue3} </SubValue>}
                                            {props.subValue4 && <SubValue>{props.subValue4} </SubValue>}
                                        </EnglishWrapper>
                                        <SpanishWrapper>
                                            <LanguageTitle>Spanish</LanguageTitle>
                                            {props.subValueSpanish && <SubValue>{props.subValueSpanish} </SubValue>}
                                            {props.subValue2Spanish && <SubValue>{props.subValue2Spanish} </SubValue>}
                                            {props.subValue3Spanish && <SubValue>{props.subValue3Spanish} </SubValue>}
                                            {props.subValue4Spanish && <SubValue>{props.subValue4Spanish} </SubValue>}
                                        </SpanishWrapper>
                                    </SubValueWrapper>
                                ) : (
                                    <SubValueWrapper>
                                        <ActionWrapper>
                                            <LanguageTitle></LanguageTitle>
                                            <SubValue>Scheduled</SubValue>
                                            <SubValue>Rescheduled</SubValue>
                                            <SubValue>Cancelled</SubValue>
                                            <SubValue>Checked Next Appt</SubValue>
                                        </ActionWrapper>
                                        <EnglishWrapper>
                                            <LanguageTitle></LanguageTitle>
                                            {props.subValue && <SubValue>{props.subValue} </SubValue>}
                                            {props.subValue2 && <SubValue>{props.subValue2} </SubValue>}
                                            {props.subValue3 && <SubValue>{props.subValue3} </SubValue>}
                                            {props.subValue4 && <SubValue>{props.subValue4} </SubValue>}
                                        </EnglishWrapper>
                                    </SubValueWrapper>
                                )
                            ) : (
                                <>
                                    {props.subValue && <SubValue>{props.subValue} </SubValue>}
                                    {props.subValue2 && <SubValue>{props.subValue2} </SubValue>}
                                    {props.subValue3 && <SubValue>{props.subValue3} </SubValue>}
                                    {props.subValue4 && <SubValue>{props.subValue4} </SubValue>}
                                </>
                            )}
                        </ValueWrapper>
                    )}
                    {!isNaN(props.deltaValue) && props.deltaValue !== null && (
                        <DeltaImage src={props.deltaValue > 0 ? deltaUp : deltaDown} />
                    )}
                    {!isNaN(props.deltaValue) && props.showValue && (
                        <ItemDelta>{Math.abs(props.deltaValue)}</ItemDelta>
                    )}
                    {!isNaN(props.deltaValue) && props.showValue && props.showPercentage && (
                        <ItemSeparator />
                    )}
                    {!isNaN(props.deltaValue) && props.showPercentage && (
                        <ItemDelta>{Math.abs(props.deltaPercentage)}%</ItemDelta>
                    )}
                </ItemValuesContainer>
            </TotalItem>
        </ConditionalTooltipWrapper>
    );

    return props.onClick ? (
        <ButtonWrapper onClick={props.onClick}>
            <Container
                className={props.className}
                hasSpanishCalls={props.hasSpanishCalls}
                isActionCompleteHealthCareCard={props.isActionCompleteHealthCareCard}
            >
                {content}
            </Container>
        </ButtonWrapper>
    ) : (
        <Container
            className={props.className}
            hasSpanishCalls={props.hasSpanishCalls}
            isActionCompleteHealthCareCard={props.isActionCompleteHealthCareCard}
        >
            {content}
        </Container>
    );
}

const ActionWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    flex-wrap: nowrap;
    flex: 0 0 auto;
    width: fit-content;
    min-width: fit-content;
    overflow: visible;
`;

const Container = styled.div<{ hasSpanishCalls?: boolean; isActionCompleteHealthCareCard?: boolean }>`
    height: 96px;
    flex: 1;
    font-family: Muli;
    border: 1px solid #CBCCD2;
    box-sizing: border-box;
    border-radius: 8px;
    padding: ${props => props.isActionCompleteHealthCareCard && props.hasSpanishCalls ? '0 4px' : '0 32px'};
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 16px;
    box-shadow: 0px 2px 4px rgba(50, 70, 97, 0.1);
    overflow: hidden;
`;

const TotalItem = styled.div<{ highlight?: boolean }>`
    flex: 1; 
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 100%;
    width: 100%;
    gap: 0px;
    font-weight: ${props => props.highlight ? 'bold' : 'normal'};
    color: ${props => props.highlight ? color_colors_ocean : 'inherit'};
    overflow: hidden;
`;

const ItemLabel = styled.p`
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${color_text_light};
    margin: 0;
    text-align: center;
`;

const ItemValuesContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 0;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
`;

const ValueWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 100%;
    overflow: hidden;
`;

const ItemValue = styled.p<{ noIcon?: boolean }>`
    font-size: 16px;
    margin: 0;
    margin-bottom: ${props => props.noIcon ? '12px' : '0'};
    text-align: center;
    font-weight: 600;
    word-wrap: break-word;
    overflow-wrap: break-word;
`;

const SubValue = styled.span`
    color: ${color_text_light};
    font-size: 12px;
    line-height: 14px;
    margin-top: 2px;
    word-wrap: break-word;
    overflow-wrap: break-word;
`;

const DeltaImage = styled.img`
    margin-left: 8px;
    margin-right: 4px;
    flex-shrink: 0;
`;

const ItemDelta = styled.p`
    color: ${color_text_light};
    font-size: 14px;
    line-height: 20px;
    margin: 0;
    white-space: nowrap;
`;

const ItemSeparator = styled.div`
    height: 16px;
    width: 1px;
    background: ${color_text_light};
    margin: 0 8px;
`;

const LanguageTitle = styled.div`
    font-size: 8px;
    min-height: 12px;
    line-height: 12px;
    white-space: nowrap;
    overflow: visible;
    margin: 0;
    padding: 0;
`;

const EnglishWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    flex: 1;
    min-width: 0;
    overflow: visible;
`;

const SpanishWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    flex: 1;
    min-width: 0;
    overflow: visible;
`;

const ButtonWrapper = styled.button`
    background: none;
    border: none;
    padding: 0;
    width: 100%;
    cursor: pointer;
    &:hover {
        opacity: 0.8;
    }
    &:disabled {
        cursor: default;
        opacity: 1;
    }
`;

const SubValueWrapper = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: -8px;
    padding: 2px;
    max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    gap: 2px;
`;

const StyledIcon = styled.img`
    height: 32px;
    width: 32px;
    flex-shrink: 0;
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: center;
    padding: 14px 2px 0px 2px;
    max-width: 100%;
`;

export default TotalCard;